/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useEffect, useState } from 'react';

// Mantine
import {
	Box,
	Title,
	Radio as NativeRadio,
	Group,
	Stack,
	LoadingOverlay,
	Tooltip,
} from '@mantine/core';

// Components
import { Select } from '@components/atoms/Select';
import { Button } from '@components/atoms/Button';
import { Text } from '@components/atoms/Text';

// Styles
import classes from './AddAdditionalityProjectForm.module.css';

// Utils
import { convertArrayToLabelValue } from '@utils/functions';

// I18n
import { useTranslation } from 'react-i18next';

import {
	useElementSize,
	useMediaQuery,
} from '@mantine/hooks';
import {
	Divider,
	FileInput,
	NumericInput,
	Radio,
	TextInputV2,
} from '@components/atoms';

import { EUROPEAN_COUNTRIES } from '@config/constants/european-countries';
import { useProducers } from 'shared/api/get-producers-has-additionalities';
import { PROJECT_TECHNOLOGIES } from '@config/constants/project-technologies';
import { UNITS_PROJECT } from '@config/constants/units-project';

import { DropzoneMultipleFiles } from '@components/molecules/DropzoneMultipleFiles';
import { getCurrencyUnit } from '@utils/currency-units';
import { TYPES_FINANCING } from '@config/constants/types-financing';
import { DatePicker } from '@components/atoms/DatePicker';

import dayjs from 'dayjs';
import { RichTextEditor } from '@components/molecules/RichTextEditor';
import { useAdditionalityProjectCreate } from '@features/resources/additionalities/hooks/useAdditionalityProjectCreate';
import { useOrganizationsHasAdditionalities } from '@api/get-organizations-has-additionalities';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddAdditionalityProjectForm = (props: {
	form: any;
	onCancel: () => void;
	onBack: () => void;
	isPreviousMeasurabilityProjectDetails: boolean;
}) => {
	const {
		isLoadingPublicTechnicalDocuments,
		setIsLoadingPublicTechnicalDocuments,
		uploadMediaLogo,
		uploadMediaCoverUrl,
		handleFileLogo,
		handleFileCoverUrl,
		handleSubmitForm,
		convertArrayTechnologiesToLabelValue,
		addAdditionalityProject,
		handleUploadFiles,
		handleReloadFiles,
	} = useAdditionalityProjectCreate({ form: props.form });

	const [, setProducerId] = useState('');

	const { t } = useTranslation();

	const { allProducers: producers } = useProducers();

	const isBigScreen = useMediaQuery('(min-width: 120em)');

	const { ref, width } = useElementSize();

	const { data: organizations } =
		useOrganizationsHasAdditionalities();

	useEffect(() => {
		if (addAdditionalityProject.isSuccess) {
			props.onBack();
		}
	}, [addAdditionalityProject.isSuccess]);

	const handleChangeSupportedBy = (
		value: string,
		form: any
	) => {
		form.setFieldValue('supportedBy', value);
	};

	const {
		onChange: onChangeProject,
		...organizationProps
	} = props.form.getInputProps('organization');

	const { onChange: onChangeSite, ...entityProps } =
		props.form.getInputProps('entity');

	const handleChangeEntity = (value: string) => {
		props.form.setFieldValue('entity', value);
		props.form.setFieldValue('project', null);
		setProducerId(value);
	};

	const {
		onChange: onChangePublicTechnicalDocuments,
		...technicalDocumentsProps
	} = props.form.getInputProps('technicalDocuments');

	const {
		onChange: onChangeLogo,
		error: errorLogo,
		...logoProps
	} = props.form.getInputProps('logo');

	const {
		onChange: onChangeCoverUrl,
		error: errorCoverUrl,
		...coverProps
	} = props.form.getInputProps('cover');

	const {
		onChange: onChangeSupportedBy,
		...supportedByProps
	} = props.form.getInputProps('supportedBy');

	/* ******************************** RENDERING ******************************* */
	return (
		<Box className={classes.container}>
			<Box className={classes.containerEmpty} />
			<form
				className={classes.containerForm}
				onSubmit={props.form.onSubmit(
					(values: any) => {}
				)}
			>
				<Box className={classes.containerTitle}>
					<Title className={classes.titleForm}>
						{t('global.projects')}
					</Title>
					<Text className={classes.subTitleForm}>
						/{' '}
						{t(
							'additionalityProjects.addAdditionalityProject'
						)}
					</Text>
				</Box>

				<NativeRadio.Group
					value={'existingProducer'}
					name="addProject"
					withAsterisk
					onChange={() => {}}
				>
					<Group gap={'47px'}>
						<Radio
							variant="outline"
							size={isBigScreen ? 'sm' : 'xs'}
							label={t('global.selectAnExistingProducer')}
							value={'existingProducer'}
						/>
						<Radio
							disabled
							variant="outline"
							size={isBigScreen ? 'sm' : 'xs'}
							label={t('global.addNewProducer')}
							value={'newProducer'}
						/>
					</Group>
				</NativeRadio.Group>
				{/* Row for Producer */}
				<Box ref={ref} className={classes.row}>
					<Box className={classes.containerInput}>
						<Select
							size={isBigScreen ? 'md' : 'sm'}
							label={t('global.producer')}
							withAsterisk
							data={convertArrayToLabelValue(producers)}
							placeholder={t('global.selectProducer')}
							onChange={handleChangeEntity}
							{...entityProps}
						/>
					</Box>
				</Box>

				{/* Row for organization */}
				{props.form.getValues().entity !== '' &&
					props.form.getValues().entity !== null && (
						<Box ref={ref} className={classes.row}>
							<Box className={classes.containerInput}>
								<Select
									size={isBigScreen ? 'md' : 'sm'}
									label={t('global.supportedBy')}
									data={convertArrayToLabelValue(
										organizations
									)}
									placeholder={t(
										'global.selectOrganization'
									)}
									onChange={(entity: string) => {
										handleChangeSupportedBy(
											entity,
											props.form
										);
									}}
									{...supportedByProps}
								/>
							</Box>
						</Box>
					)}

				<Divider size={'xs'} />
				{props.form.getValues().entity !== '' &&
					props.form.getValues().entity !== null && (
						<Stack pos="relative">
							<LoadingOverlay
								visible={
									uploadMediaLogo.isLoading ||
									uploadMediaCoverUrl.isLoading ||
									isLoadingPublicTechnicalDocuments
								}
								zIndex={1000}
								overlayProps={{ radius: 'sm', blur: 1 }}
							/>
							<Box className={classes.rowFcDetails}>
								<Title className={classes.titleFcDetails}>
									{t('Project details')}
								</Title>
							</Box>

							{/* Row for name */}
							<Box className={classes.containerInput}>
								<TextInputV2
									withAsterisk
									size={isBigScreen ? 'md' : 'sm'}
									label={t('global.name')}
									placeholder={t(
										'placeholders.projectName'
									)}
									{...props.form.getInputProps('name')}
								/>
							</Box>

							{/* Row for Country & Place */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.country')}
										data={EUROPEAN_COUNTRIES}
										placeholder={t(
											'placeholders.selectCountry'
										)}
										{...props.form.getInputProps(
											'countryCode'
										)}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<TextInputV2
										disabled={
											!props.form.values.countryCode
										}
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.place')}
										placeholder={t('global.place')}
										{...props.form.getInputProps('place')}
									/>
								</Box>
							</Box>

							{/* Row for Unit & Technology */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.labels.unit')}
										data={UNITS_PROJECT}
										placeholder={t(
											'placeholders.selectUnit'
										)}
										{...props.form.getInputProps('unit')}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.technology')}
										data={convertArrayTechnologiesToLabelValue(
											PROJECT_TECHNOLOGIES
										)}
										placeholder={t(
											'placeholders.selectTechnology'
										)}
										{...props.form.getInputProps(
											'technology'
										)}
									/>
								</Box>
							</Box>

							{/* Row for Date due */}
							<Box className={classes.row}>
								<Box
									className={classes.containerFileInput}
								>
									<Select
										withAsterisk
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.typeOfFinancing')}
										data={convertArrayTechnologiesToLabelValue(
											TYPES_FINANCING
										)}
										placeholder={t(
											'placeholders.selectTypeOfFinancing'
										)}
										{...props.form.getInputProps(
											'typeOfFinancing'
										)}
									/>
								</Box>
								<Box className={classes.containerInput}>
									<NumericInput
										disabled={
											props.form.getValues()
												.typeOfFinancing == 'UNLIMITED'
										}
										withAsterisk={
											props.form.getValues()
												.typeOfFinancing != 'UNLIMITED'
										}
										decimalScale={2}
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.financialNeeds')}
										defaultValue={0}
										unit={getCurrencyUnit('EUR')}
										{...props.form.getInputProps(
											'financing'
										)}
									/>
								</Box>
							</Box>

							{/* Row for sellPrice & costPrice */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<DatePicker
										disabled={
											props.form.getValues()
												.typeOfFinancing == 'UNLIMITED'
										}
										withAsterisk
										clearable
										size={isBigScreen ? 'lg' : 'sm'}
										label={t('global.dueDate')}
										placeholder={t('global.selectDate')}
										minDate={dayjs()
											.add(1, 'day')
											.toDate()}
										onChange={() => {}}
										{...props.form.getInputProps(
											'dueDate'
										)}
									/>
								</Box>

								<Box className={classes.containerInput}>
									<DatePicker
										withAsterisk={
											props.form.getValues()
												.typeOfFinancing !== 'UNLIMITED'
										}
										clearable
										size={isBigScreen ? 'lg' : 'sm'}
										label={t(
											'global.dateOfCommissioning'
										)}
										placeholder={t('global.selectDate')}
										{...props.form.getInputProps(
											'openingDate'
										)}
									/>
								</Box>
							</Box>

							{/* Row for logo & cover */}
							<Box className={classes.row}>
								<Box
									className={classes.containerFileInput}
								>
									<FileInput
										maxsize={20}
										accept="image/png,image/jpeg"
										error={errorLogo}
										clearable
										onChange={(file: File | null) => {
											handleFileLogo(file, props.form);
										}}
										label={t('global.logo')}
										size={isBigScreen ? 'md' : 'sm'}
									/>
								</Box>

								<Box
									className={classes.containerFileInput}
								>
									<FileInput
										maxsize={20}
										accept="image/png,image/jpeg"
										withAsterisk
										clearable
										onChange={(file: File | null) => {
											handleFileCoverUrl(
												file,
												props.form
											);
										}}
										label={t('global.coverImage')}
										size={isBigScreen ? 'md' : 'sm'}
										error={errorCoverUrl}
									/>
								</Box>
							</Box>

							{/* Row for Dropzone multiplefiles */}
							<Tooltip
								label={t('global.accessibleToPublic')}
							>
								<Box className={classes.row}>
									<DropzoneMultipleFiles
										isPublicFile={true}
										entity={props.form.getValues().entity}
										handleUpload={(files: any) => {
											handleUploadFiles(
												files,
												props.form
											);
										}}
										handleReload={(
											filteredFiles: any
										) => {
											handleReloadFiles(
												filteredFiles,
												props.form
											);
										}}
										label={t('global.technicalDocuments')}
										getUploadStatus={(status: any) => {
											if (!status) {
												setIsLoadingPublicTechnicalDocuments(
													true
												);
											}
											if (status) {
												setIsLoadingPublicTechnicalDocuments(
													false
												);
											}
										}}
									/>
								</Box>
							</Tooltip>

							<Divider size={'xs'} />

							{/* Row for Linkedin & X */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('X Url')}
										placeholder={'https://www.x.com/'}
										{...props.form.getInputProps(
											'twitter'
										)}
									/>
								</Box>

								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('Linkedin Url')}
										placeholder={
											'https://www.linkedin.com/'
										}
										{...props.form.getInputProps(
											'linkedin'
										)}
									/>
								</Box>
							</Box>
							{/* Row for website */}
							<Box className={classes.row}>
								<Box className={classes.containerInput}>
									<TextInputV2
										size={isBigScreen ? 'md' : 'sm'}
										label={t('global.website')}
										placeholder={
											'https://www.example.com/'
										}
										{...props.form.getInputProps(
											'website'
										)}
									/>
								</Box>
							</Box>

							{/* Row for description */}
							<Box className={classes.row}>
								<RichTextEditor
									size="md"
									label={t('global.description')}
									{...props.form.getInputProps(
										'description'
									)}
									maxWidth={width}
									placeholder={t('global.description')}
								/>
							</Box>
							<Divider size="xs" />
							{/* Row for Buttons */}
							<Box className={classes.rowButtons}>
								<Box
									className={
										classes.containerButtonCancel
									}
								>
									<Button
										fullWidth
										size={isBigScreen ? 'lg' : 'md'}
										variant="transparent"
										onClick={() => {
											props.onCancel();
										}}
									>
										{t('global.cancel')}
									</Button>
								</Box>
								<Box className={classes.containerButton}>
									<Button
										data-autofocus
										fullWidth
										size={isBigScreen ? 'lg' : 'md'}
										onClick={async () => {
											await handleSubmitForm(props.form);
										}}
										loading={
											addAdditionalityProject.isLoading
										}
									>
										{t(
											'additionalityProjects.addAdditionalityProject'
										)}
									</Button>
								</Box>
							</Box>
						</Stack>
					)}
			</form>
			<Box className={classes.containerEmpty} />
		</Box>
	);
};
